import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Search, Sparkles, Send, LineChart, Bot, Plug, Users, Wallet, Settings } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import { trackPageview } from "@/lib/visits";

import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";

import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Playbook from "@/pages/Playbook";
import AppLayout from "@/components/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import ModuleStub from "@/pages/ModuleStub";
import FindLayout from "@/pages/find/FindLayout";
import LeadsList from "@/pages/find/LeadsList";
import LeadDetail from "@/pages/find/LeadDetail";
import AIProspect from "@/pages/find/AIProspect";
import Segments from "@/pages/find/Segments";
import ImportCsv from "@/pages/find/ImportCsv";
import EngageLayout from "@/pages/engage/EngageLayout";
import CampaignsList from "@/pages/engage/CampaignsList";
import CampaignWizard from "@/pages/engage/CampaignWizard";
import CampaignDetail from "@/pages/engage/CampaignDetail";
import Templates from "@/pages/engage/Templates";
import Inbox from "@/pages/engage/Inbox";
import Analyze from "@/pages/Analyze";
import Enrich from "@/pages/Enrich";
import Copilot from "@/pages/Copilot";
import Integrations from "@/pages/Integrations";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import Referrals from "@/pages/Referrals";
import Autopilot from "@/pages/Autopilot";
import SettingsPage from "@/pages/Settings";
import Docs from "@/pages/Docs";

// Handle Emergent OAuth callback at ANY route with #session_id=
function OAuthGate({ children }) {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return children;
}

// Fire anonymous pageview on every route change (public + app).
function PageviewTracker() {
    const location = useLocation();
    useEffect(() => {
        trackPageview(location.pathname);
    }, [location.pathname]);
    return null;
}

function AppRoutes() {
    return (
        <OAuthGate>
            <PageviewTracker />
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                <Route path="/playbook" element={<Playbook />} />
                <Route path="/login" element={<AuthPage mode="login" />} />
                <Route path="/register" element={<AuthPage mode="register" />} />

                <Route element={<AppLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/app" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/app/find" element={<FindLayout />}>
                        <Route index element={<Navigate to="/app/find/leads" replace />} />
                        <Route path="leads" element={<LeadsList />} />
                        <Route path="leads/:leadId" element={<LeadDetail />} />
                        <Route path="prospect" element={<AIProspect />} />
                        <Route path="segments" element={<Segments />} />
                        <Route path="import" element={<ImportCsv />} />
                    </Route>
                    <Route path="/app/enrich" element={<Enrich />} />
                    <Route path="/app/engage" element={<EngageLayout />}>
                        <Route index element={<Navigate to="/app/engage/campaigns" replace />} />
                        <Route path="campaigns" element={<CampaignsList />} />
                        <Route path="campaigns/new" element={<CampaignWizard />} />
                        <Route path="campaigns/:campaignId" element={<CampaignDetail />} />
                        <Route path="templates" element={<Templates />} />
                        <Route path="inbox" element={<Inbox />} />
                    </Route>
                    <Route path="/app/analyze" element={<Analyze />} />
                    <Route path="/app/copilot" element={<Copilot />} />
                    <Route path="/app/integrations" element={<Integrations />} />
                    <Route path="/app/team" element={<Team />} />
                    <Route path="/app/billing" element={<Billing />} />
                    <Route path="/app/referrals" element={<Referrals />} />
                    <Route path="/app/autopilot" element={<Autopilot />} />
                    <Route path="/app/docs" element={<Docs />} />
                    <Route path="/app/settings" element={<SettingsPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </OAuthGate>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <I18nProvider>
                    <AuthProvider>
                        <AppRoutes />
                        <Toaster theme="dark" position="top-center" richColors />
                    </AuthProvider>
                </I18nProvider>
            </BrowserRouter>
        </div>
    );
}
